import { useLocation, Link as RouterLink } from 'react-router-dom';
import React from 'react';
import { AppRoutes } from '@consts/routes';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { colors, typography as typographyTokens } from '@components/design-system/tokens';

/**
 * This is a map of routes to breadcrumb labels.
 * If a route is not in this map, it will not be displayed in the breadcrumb.
 */
const BREADCRUMB_LABELS: Record<string, string> = {
    [AppRoutes.ManageCompany]: 'Manage Company',
    [AppRoutes.ManageReseller]: 'Manage Reseller',
    [AppRoutes.ManageLocation]: 'Manage Location',
    [AppRoutes.Activity]: 'Cut Activity',
    [AppRoutes.Insights]: 'Insights',
    [AppRoutes.Models]: 'Manage Models',
    [AppRoutes.Design]: 'Manage Design',
    [AppRoutes.ManageUser]: 'Manage User',
    [AppRoutes.Users]: 'Manage Users',
    [AppRoutes.CutHistory]: 'Cut History',
    [AppRoutes.Organizations]: 'Organizations',
    [AppRoutes.BatchProcessing]: 'Batch Processing',
    [AppRoutes.Invoices]: 'Invoice Management',
    [AppRoutes.Employees]: 'Employee Reports',
    [AppRoutes.EmployeeDetails]: 'Employee Details',
    [AppRoutes.BillingChanges]: 'Billing Changes',
    [AppRoutes.StationMoveActivity]: 'Station Move Activity',
    [AppRoutes.AuditTrail]: 'Audit History',
    [AppRoutes.AdminAuditLog]: 'Admin Audit Log',
    [AppRoutes.MaterialProductManagement]: 'Product Management',
    [AppRoutes.TodaysCuts]: "Today's Cuts",
    [AppRoutes.Resellers]: 'Reseller Reports',
    [AppRoutes.ResellersSecondary]: 'Reseller Reports',
    [AppRoutes.Locations]: 'Location Reports',
    [AppRoutes.Products]: 'Product Reports',
    [AppRoutes.Issues]: 'Issues',
};

// Map query param values to their corresponding breadcrumb labels.
const MANAGE_TYPE_LABELS: Record<string, string> = {
    company: 'Company',
    reseller: 'Reseller',
    location: 'Location',
    system: 'System',
    employee: 'Employee',
};

const BreadcrumbLink: React.FC<{
    to: string;
    children: React.ReactNode;
    isLast?: boolean;
}> = ({ to, children, isLast }) => (
    <Link
        component={RouterLink}
        to={to}
        underline='hover'
        sx={{
            color: isLast ? colors.neutral[900] : colors.primary[600],
            fontWeight: isLast ? typographyTokens.fontWeight.semibold : typographyTokens.fontWeight.medium,
            fontSize: typographyTokens.fontSize.sm,
            textDecoration: 'none',
            '&:hover': {
                color: colors.primary[500],
                textDecoration: 'underline',
            },
        }}>
        {children}
    </Link>
);

export const RouteBreadcrumb = () => {
    const location = useLocation();

    // Query params are required for the dynamic "Manage X" breadcrumb.
    const queryParams = new URLSearchParams(location.search);
    const typeParam = queryParams.get('type')?.toLowerCase() ?? '';
    const dynamicLabel = MANAGE_TYPE_LABELS[typeParam];

    const currentRoute = location.pathname;
    const pageLabel = BREADCRUMB_LABELS[currentRoute];

    // Build breadcrumb items - only clickable items
    type BreadcrumbItem = { label: string; route: string; isLast: boolean };
    const items: BreadcrumbItem[] = [];

    // On home page, just show "Home" as the only item
    if (currentRoute === AppRoutes.Insights) {
        items.push({ label: 'Home', route: AppRoutes.Insights, isLast: true });
    } else {
        // Always start with Home (clickable)
        items.push({ label: 'Home', route: AppRoutes.Insights, isLast: false });

        // Add current page if it has a label
        if (pageLabel) {
            // If there's a dynamic label coming, this isn't the last item
            const isLast = !dynamicLabel;
            items.push({ label: pageLabel, route: currentRoute, isLast });
        }

        // Add dynamic label if present (e.g., "Company" for /manage/organizations?type=company)
        if (dynamicLabel && currentRoute === AppRoutes.Organizations) {
            items.push({
                label: dynamicLabel,
                route: `${currentRoute}${location.search}`,
                isLast: true,
            });
        }
    }

    return (
        <Breadcrumbs
            separator={
                <Typography
                    sx={{
                        color: colors.neutral[300],
                        fontSize: typographyTokens.fontSize.sm,
                        mx: 0.5,
                    }}>
                    /
                </Typography>
            }
            sx={{
                '& .MuiBreadcrumbs-ol': {
                    alignItems: 'center',
                },
            }}>
            {items.map((item, index) => (
                <BreadcrumbLink key={`${item.label}-${index}`} to={item.route} isLast={item.isLast}>
                    {item.label}
                </BreadcrumbLink>
            ))}
        </Breadcrumbs>
    );
};
